import { VBCQueryAlert } from "../types/vbc-alerts";

const normalizeCommentText = (value?: string | null) =>
  value?.replace(/\r\n/g, "\n").trim() ?? "";

const buildEvidenceReference = (
  provider?: string | null,
  _dos?: string | null,
  page?: string | null
) => {
  // DOS is shown in its own table column; keep it out of the italic secondary text.
  const referenceParts = [
    provider ? `Dr.${provider}` : "",
    page ? `(page ${page})` : "",
  ].filter(Boolean);

  return referenceParts.join(" ");
};

const buildEvidenceLine = (
  evidenceText?: string | null,
  provider?: string | null,
  dos?: string | null,
  page?: string | null
) => {
  const text = normalizeCommentText(evidenceText);
  const reference = buildEvidenceReference(provider, dos, page);

  if (!text) {
    return "";
  }

  return reference ? `${text} - ${reference}` : text;
};

const buildEvidenceText = (
  provider?: string | null,
  dos?: string | null,
  evidences?: { text?: string | null; page?: string | null }[] | null,
  fallbackEvidence?: string | null,
  fallbackPage?: string | null
) => {
  const evidenceLines =
    evidences
      ?.map((item) => buildEvidenceLine(item?.text, provider, dos, item?.page))
      .filter(Boolean) ?? [];

  if (evidenceLines.length > 0) {
    return evidenceLines.join("\n\n");
  }

  return buildEvidenceLine(fallbackEvidence, provider, dos, fallbackPage);
};

export const extractVBCQueryCommentParts = (
  query: Pick<
    VBCQueryAlert,
    "comments" | "createdAt" | "dos" | "evidence" | "evidences" | "page" | "provider" | "queryComment"
  >
) => {
  const fallbackComment = normalizeCommentText(query.comments);
  const fallbackCreatedAt = normalizeCommentText(query.createdAt);
  const fallbackDos = normalizeCommentText(query.dos);
  const fallbackEvidence = buildEvidenceText(
    query.provider,
    query.dos,
    query.evidences,
    query.evidence,
    query.page
  );
  const normalizedValue = normalizeCommentText(query.queryComment);

  if (!normalizedValue) {
    return {
      queryComment: fallbackComment,
      queryEvidence: fallbackEvidence,
      createdAt: fallbackCreatedAt,
      dos: fallbackDos,
    };
  }

  try {
    const parsedValue = JSON.parse(normalizedValue);

    if (typeof parsedValue === "string") {
      return {
        queryComment: normalizeCommentText(parsedValue),
        queryEvidence: "",
        createdAt: fallbackCreatedAt,
        dos: fallbackDos,
      };
    }

    if (parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)) {
      const comments =
        typeof parsedValue.comments === "string" ? parsedValue.comments : undefined;
      const createdAt =
        typeof parsedValue.createdAt === "string"
          ? parsedValue.createdAt
          : query.createdAt;
      const provider =
        typeof parsedValue.provider === "string" ? parsedValue.provider : query.provider;
      const dos = typeof parsedValue.dos === "string" ? parsedValue.dos : query.dos;
      const page = typeof parsedValue.page === "string" ? parsedValue.page : query.page;
      const evidence =
        typeof parsedValue.evidence === "string" ? parsedValue.evidence : query.evidence;
      const evidences = Array.isArray(parsedValue.evidences)
        ? parsedValue.evidences
        : query.evidences;

      return {
        queryComment: normalizeCommentText(comments) || fallbackComment,
        queryEvidence:
          buildEvidenceText(provider, dos, evidences, evidence, page) || fallbackEvidence,
        createdAt: normalizeCommentText(createdAt) || fallbackCreatedAt,
        dos: normalizeCommentText(dos) || fallbackDos,
      };
    }
  } catch {
    return {
      queryComment: normalizedValue,
      queryEvidence: "",
      createdAt: fallbackCreatedAt,
      dos: fallbackDos,
    };
  }

  return {
    queryComment: fallbackComment,
    queryEvidence: fallbackEvidence,
    createdAt: fallbackCreatedAt,
    dos: fallbackDos,
  };
};
